/* Courted · the pure parts (tests/courted.test.js): the kinds of place, how far you'll go, what a card says,
   the deck's order, your ideas, the free times this week and which idea fits each, Plan it's days and
   defaults, the sun, and the words that change by country. No window, no network. */

/* Something fun (bowling, escape rooms, pool, a class) and culture (galleries, museums, cinemas, shows) took the
   place of "out and about" on 29 Oct (Ben: "very distinct things that take a different mood"); a row swiped as
   'out' before reads as culture — the old deck's out was live music, galleries and museums. */
export const KINDS = [
  { id: 'restaurant', label: 'Restaurants', one: 'Dinner' },
  { id: 'bar', label: 'Bars', one: 'Drinks' },
  { id: 'fun', label: 'Something fun', one: 'Something fun' },
  { id: 'culture', label: 'Culture', one: 'Culture' },
  { id: 'outdoors', label: 'Outdoors', one: 'Outdoors' },
];
export const KIND_IDS = KINDS.map((k) => k.id);
export const RANGES = [[15, '15 min'], [30, '30 min'], [60, 'An hour'], [90, '90 min']];
// How long a date of each kind usually takes (Plan it's "For about"), unless the catalogue says.
export const USUAL_MINUTES = { restaurant: 120, bar: 90, fun: 120, culture: 120, out: 120, outdoors: 180 };

/* ---- the kind of place, from Google's primary type when the catalogue doesn't say ---- */
const BAR = /^(bar|cocktail_bar|wine_bar|pub|lounge_bar|sports_bar|beer_hall|brewpub|brewery|irish_pub|hookah_bar|night_club|bar_and_grill)$/;
const OUTDOORS = /^(hiking_area|park|national_park|state_park|nature_preserve|garden|botanical_garden|beach|campground|scenic_spot|lake|river|waterfall|wildlife_park|wildlife_refuge|woods|mountain_peak|picnic_ground|dog_park|marina)$/;
const CULTURE = /^(art_gallery|art_museum|museum|history_museum|movie_theater|performing_arts_theater|concert_hall|opera_house|philharmonic_hall|amphitheatre|comedy_club|cultural_center|cultural_landmark|historical_landmark|historical_place|monument|planetarium|library|live_music_venue|jazz_club|auditorium|castle)$/;
export function kindOfType(type) {
  const t = String(type || '');
  if (BAR.test(t)) return 'bar';
  if (OUTDOORS.test(t)) return 'outdoors';
  if (/restaurant$|^bistro$|^steak_house$|^brasserie$|^diner$|^gastropub$|^food_court$|^fine_dining/.test(t)) return 'restaurant';
  if (CULTURE.test(t)) return 'culture';
  return 'fun';   // bowling, an escape room, a market, a class, an arcade…
}
/** 'out' (before 29 Oct) as today's kinds: by Google's type when we have it, else culture. */
export const normKind = (k, card) => (k === 'out' ? (card && card.type ? kindOfType(card.type) : 'culture') : k);
export const kindOf = (id, cat, card, swipeKind) => normKind((cat && cat.kind) || swipeKind || (card ? kindOfType(card.type) : null) || 'fun', card);

/* "Dinner", "Cocktail bar", "Hike", "Art museum": ours when the catalogue has the place, else Google's type. */
export function whatOf(card, cat, words) {
  if (cat && cat.what) return cat.what;
  const k = kindOf(null, cat, card);
  if (k === 'restaurant') return 'Dinner';
  if (card && card.type === 'hiking_area') return words && words.hike === 'walk' ? 'Walk' : 'Hike';
  return (card && card.typeLabel) || KINDS.find((x) => x.id === k).one;
}

/* The street or the neighbourhood from Google's short address: "357 Barton St E, Hamilton" → "Barton St E";
   "Lower, 150 James St S, Hamilton" → "James St S"; "Escarpment Rail Trail, Hamilton" → "Escarpment Rail Trail". */
export function areaOf(address) {
  const parts = String(address || '').split(',').map((s) => s.trim()).filter(Boolean);
  if (!parts.length) return '';
  const street = parts.length > 1 ? parts[parts.length - 2] : parts[0];
  return street.replace(/^(unit|suite|ste\.?|#)\s*\S+\s+/i, '').replace(/^\d+[a-z]?(-\d+)?\s+/i, '').trim() || street;
}
export const townOf = (address) => { const p = String(address || '').split(',').map((s) => s.trim()).filter(Boolean); return p.length > 1 ? p[p.length - 1] : ''; };

/* "Bar · Barton St E · 8 min" */
export function cardLine(card, cat, words) {
  const what = whatOf(card, cat, words);
  const area = card ? areaOf(card.address) : '';
  return [what, area, card && card.minutes ? card.minutes + ' min' : null].filter(Boolean).join(' · ');
}

/* ---- the deck ---- */
/* Your partner's yeses you haven't answered first, then the catalogue's places, then Google's, the kinds taken
   in turn so it isn't all restaurants; "Not sure yet" ones come back at the end. Anything you've answered
   otherwise (yes, no, been there) is gone. → [{ id, kind, partnerYes }] */
export function buildDeck({ ids = {}, cats = [], mine = {}, theirs = {}, theirKinds = {}, kinds = KIND_IDS } = {}) {
  const want = new Set(kinds && kinds.length ? kinds : KIND_IDS);
  const out = [], seen = new Set();
  const catById = {};
  cats.forEach((c) => { catById[c.place_id] = c; });
  const add = (id, kind, partnerYes) => {
    if (!id || seen.has(id)) return;
    const k = normKind((catById[id] && catById[id].kind) || kind, null);
    if (k && !want.has(k)) return;
    if (mine[id] && mine[id] !== 'later') return;
    seen.add(id);
    out.push({ id, kind: k || null, partnerYes: !!partnerYes, partnerLoved: theirs[id] === 'loved', later: mine[id] === 'later' });
  };
  // your partner's yeses and favourites first ("Olya said yes", "Olya loved it")
  Object.keys(theirs).filter((id) => yesLike(theirs[id])).forEach((id) => add(id, theirKinds[id], true));
  roundRobin(KIND_IDS.map((k) => cats.filter((c) => normKind(c.kind) === k).map((c) => c.place_id))).forEach((id) => add(id, null, yesLike(theirs[id])));
  roundRobin(KIND_IDS.map((k) => (ids[k] || []).map((id) => [id, k]))).forEach(([id, k]) => add(id, k, yesLike(theirs[id])));
  return [...out.filter((x) => !x.later), ...out.filter((x) => x.later)];
}
function roundRobin(lists) {
  const out = [];
  const n = Math.max(0, ...lists.map((l) => l.length));
  for (let i = 0; i < n; i++) lists.forEach((l) => { if (l[i] !== undefined) out.push(l[i]); });
  return out;
}

/* ---- Swipe for a kind of date, and a free time (Ben, 29 Oct: "say what type of date … up front, and if it's for
   a specific date") ---- */
export const DATE_KINDS = [['restaurant', 'Dinner'], ['bar', 'Drinks'], ['fun', 'Something fun'], ['culture', 'Culture'], ['outdoors', 'Outdoors']];
/** Tap a kind: from everything, just that one; the last one off (or all of them on), everything again. */
export function toggleKind(kinds, id) {
  const cur = kinds && kinds.length ? kinds : [];
  if (!cur.length) return [id];
  const next = cur.includes(id) ? cur.filter((k) => k !== id) : [...cur, id];
  return next.length === KIND_IDS.length ? [] : next;
}
/** The kinds the deck shows: what you picked (or all of them), less what doesn't fit the free time you're swiping
    for — outdoors after dark, or on a wet day. → { kinds, left } (left: the kinds that don't fit) */
export function kindsFor(kinds, slot, sunset, sky) {
  const want = kinds && kinds.length ? kinds : KIND_IDS;
  if (!slot) return { kinds: want, left: [] };
  const left = KIND_IDS.filter((k) => !fits(slot, k, sunset) || (k === 'outdoors' && sky === 'wet'));
  return { kinds: want.filter((k) => !left.includes(k)), left };
}
/** Under the kinds: "For Friday from 6:30pm. It's dark by then, so no hikes." / "For Saturday, all day." */
export function forLine(slot, left, words, sky) {
  if (!slot) return null;
  const head = 'For ' + (slot.all ? slot.dow + ', all day' : slot.dow + ' from ' + ampm(slot.from)) + '.';
  if (!(left || []).includes('outdoors')) return head;
  return head + ' ' + (sky === 'wet' ? 'It’s wet that day' : 'It’s dark by then') + ', so no ' + ((words && words.hikes) || 'Hikes').toLowerCase() + '.';
}

/* Rows → { place_id: verdict } for one person, and the kind each was swiped as. */
export function verdicts(rows, uid) {
  const v = {}, k = {};
  (rows || []).forEach((r) => { if (r.user_id === uid) { v[r.place_id] = r.verdict; if (r.kind) k[r.place_id] = r.kind; } });
  return { v, k };
}

/* After you've been (Ben, 29 Oct): Loved it is a favourite — it counts as a yes and comes up again — and Wouldn't go
   again (for now) counts as a no. Been there alone is neither. */
export const yesLike = (v) => v === 'yes' || v === 'loved';
export const noLike = (v) => v === 'no' || v === 'never';
const byPlace = (rows) => { const by = {}; (rows || []).forEach((r) => { (by[r.place_id] = by[r.place_id] || {})[r.user_id] = r; }); return by; };
const latest = (...rs) => rs.filter(Boolean).map((r) => r.updated_at || r.created_at || '').sort().pop();
const newest = (x, y) => (x.at < y.at ? 1 : x.at > y.at ? -1 : 0);
/* Your ideas: what you both said yes to or loved (a home of one: yours), newest first.
   → [{ id, kind, at, loved: 'both' | 'me' | 'them' | null }] */
export function ideasOf(rows, me, partner) {
  const by = byPlace(rows);
  const out = [];
  Object.keys(by).forEach((id) => {
    const a = by[id][me], b = partner ? by[id][partner] : null;
    if (!a || !yesLike(a.verdict)) return;
    if (partner && (!b || !yesLike(b.verdict))) return;
    const la = a.verdict === 'loved', lb = !!(b && b.verdict === 'loved');
    out.push({ id, kind: a.kind || (b && b.kind) || null, at: latest(a, b), loved: la && lb ? 'both' : la ? 'me' : lb ? 'them' : null });
  });
  return out.sort(newest);
}
/* Yours to suggest (Ben, 29 Oct: "places I like Olya hasn't said no to"): your yeses and favourites your partner
   hasn't answered yet, or isn't sure about. Plan one and they're asked. → [{ id, kind, at, loved, mine: true }] */
export function mineOf(rows, me, partner) {
  if (!partner) return [];
  const by = byPlace(rows);
  const out = [];
  Object.keys(by).forEach((id) => {
    const a = by[id][me], b = by[id][partner];
    if (!a || !yesLike(a.verdict)) return;
    if (b && (yesLike(b.verdict) || noLike(b.verdict))) return;
    out.push({ id, kind: a.kind || null, at: latest(a), loved: a.verdict === 'loved' ? 'me' : null, mine: true });
  });
  return out.sort(newest);
}
/* Your favourites: anywhere one of you loved, and neither of you said no to since. Yours, your partner's and both.
   → [{ id, kind, at, by: 'both' | 'me' | 'them' }] */
export function favouritesOf(rows, me, partner) {
  const by = byPlace(rows);
  const out = [];
  Object.keys(by).forEach((id) => {
    const a = by[id][me], b = partner ? by[id][partner] : null;
    const la = !!(a && a.verdict === 'loved'), lb = !!(b && b.verdict === 'loved');
    if (!la && !lb) return;
    if ((a && noLike(a.verdict)) || (b && noLike(b.verdict))) return;
    out.push({ id, kind: (a && a.kind) || (b && b.kind) || null, at: latest(a, b), by: la && lb ? 'both' : la ? 'me' : 'them' });
  });
  return out.sort(newest);
}
/* Where either of you wouldn't go again, for now: there to change your mind. → [{ id, kind, by }] */
export function notAgainOf(rows, me, partner) {
  const by = byPlace(rows);
  return Object.keys(by).map((id) => {
    const a = by[id][me], b = partner ? by[id][partner] : null;
    const na = !!(a && a.verdict === 'never'), nb = !!(b && b.verdict === 'never');
    return na || nb ? { id, kind: (a && a.kind) || (b && b.kind) || null, at: latest(a, b), by: na && nb ? 'both' : na ? 'me' : 'them' } : null;
  }).filter(Boolean).sort(newest);
}
/* Your partner's yeses and favourites you haven't answered: they're first in your deck. */
export function waitingOf(rows, me, partner) {
  if (!partner) return [];
  const mine = verdicts(rows, me).v;
  return (rows || []).filter((r) => r.user_id === partner && yesLike(r.verdict) && !mine[r.place_id]).map((r) => r.place_id);
}
/* Is this yes a match? (the other person said yes already, or loved it) */
export const isMatch = (rows, partner, id) => !!partner && (rows || []).some((r) => r.user_id === partner && r.place_id === id && yesLike(r.verdict));

/* ---- times and days ---- */
export function ampm(m, zeros) {
  const h = Math.floor(m / 60) % 24, mm = m % 60;
  const h12 = ((h + 11) % 12) + 1;
  return h12 + (mm || zeros ? ':' + String(mm).padStart(2, '0') : '') + (h < 12 ? 'am' : 'pm');
}
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const dateOf = (iso) => new Date(iso + 'T12:00:00');
export const addDays = (iso, n) => { const d = dateOf(iso); d.setDate(d.getDate() + n); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); };

/* Home's free times (weekFree().days: { date, from, to, all }) in the next seven days, as This week shows them:
   "Thursday 29" · "From 6:30pm" / "All day". Today counts from now on. */
export function slotsFrom(days, today) {
  const end = addDays(today, 7);
  // a stretch worth going out for: a whole free day, or two hours or more
  return (days || []).filter((d) => d.date >= today && d.date < end && (d.all || d.to - d.from >= 120)).map(slotOf);
}
function slotOf(d) {
  const dt = dateOf(d.date);
  return { date: d.date, from: d.from, to: d.to, all: !!d.all, dow: DAYS[dt.getDay()], label: DAYS[dt.getDay()] + ' ' + dt.getDate(),
    when: d.all ? 'All day' : 'From ' + ampm(d.from), short: { wd: DAYS[dt.getDay()].slice(0, 3), n: String(dt.getDate()), hrs: d.all ? 'All day' : ampm(d.from) + ' on' } };
}
/* Plan it's days with the ones you came for in them (Home's "Date night?" for Friday, Swipe for Friday, Find a
   spot's day): a day that isn't one of your free times is still offered rather than swapped for the first free one
   (feedback, 31 Oct: Friday's date night was planned for Saturday). It's `unknown` — we don't know when you're both
   free then, so it says only the day and starts at the kind's usual time. Past days aren't added. → by date */
export function withDays(slots, dates, today) {
  const out = (slots || []).slice();
  for (const date of dates || []) {
    if (!date || date < today || out.some((s) => s.date === date)) continue;
    const s = slotOf({ date, from: 18 * 60, to: 23 * 60, all: false });
    out.push({ ...s, unknown: true, when: '', short: { ...s.short, hrs: date === today ? 'Today' : '' } });
  }
  return out.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}
/* With children (Home's `care`, src/core/week-free.js): whether a free time needs a sitter (DOM-1). Two hours or more
   when nobody needs to be with the children — they're at school, or with someone else — don't, and the time starts
   when that does ("Ada’s with Grandparents", "While they’re at school"); any other time does ("You’ll need a sitter"). */
export function withCare(slots, care) {
  if (!care) return slots;
  return (slots || []).map((s) => {
    const c = care[s.date];
    if (!c) return s;
    const free = (c.free || []).map(([a, b]) => [Math.max(a, s.from), Math.min(b, s.to)]).filter(([a, b]) => b - a >= 120);
    if (!free.length) return { ...s, sitter: true, kids: 'You’ll need a sitter' };
    const a = free[0][0];
    const moved = !s.all && a > s.from;
    const kids = c.with || (a < 17 * 60 ? 'While they’re at school' : 'Just the two of you');
    return { ...s, ...(moved ? { from: a, when: 'From ' + ampm(a), short: { ...s.short, hrs: ampm(a) + ' on' } } : {}), sitter: false, kids };
  });
}
/* "Saturday 31 October" (Canada, the UK) or "Saturday, October 31" (the US). */
export const dayWords = (iso, cc) => { const d = dateOf(iso); return String(cc || '').toUpperCase() === 'US' ? DAYS[d.getDay()] + ', ' + MONTHS[d.getMonth()] + ' ' + d.getDate() : DAYS[d.getDay()] + ' ' + d.getDate() + ' ' + MONTHS[d.getMonth()]; };
/* "8 Nov" or "Nov 8" (the US). */
export const shortDate = (iso, cc) => { const d = dateOf(iso); const m = MONTHS[d.getMonth()].slice(0, 3); return String(cc || '').toUpperCase() === 'US' ? m + ' ' + d.getDate() : d.getDate() + ' ' + m; };

/* ---- the sun: when it sets at home (NOAA's approximation, good to a few minutes) ---- */
/* Minutes after midnight UTC that the sun sets on `iso` at lat/lng, or null at the poles' extremes. */
export function sunsetUTC(iso, lat, lng) {
  const d = dateOf(iso);
  const start = new Date(d.getFullYear(), 0, 0);
  const n = Math.round((d - start) / 86400000);
  const g = (2 * Math.PI / 365) * (n - 1);
  const eq = 229.18 * (0.000075 + 0.001868 * Math.cos(g) - 0.032077 * Math.sin(g) - 0.014615 * Math.cos(2 * g) - 0.040849 * Math.sin(2 * g));
  const decl = 0.006918 - 0.399912 * Math.cos(g) + 0.070257 * Math.sin(g) - 0.006758 * Math.cos(2 * g) + 0.000907 * Math.sin(2 * g) - 0.002697 * Math.cos(3 * g) + 0.00148 * Math.sin(3 * g);
  const phi = lat * Math.PI / 180;
  const c = Math.cos(90.833 * Math.PI / 180) / (Math.cos(phi) * Math.cos(decl)) - Math.tan(phi) * Math.tan(decl);
  if (c < -1 || c > 1) return null;
  const ha = Math.acos(c) * 180 / Math.PI;
  return Math.round(720 - 4 * (lng - ha) - eq);
}
/* The same in local minutes, given the local offset for that day (minutes east of UTC). */
export function sunsetLocal(iso, lat, lng, offsetEast) {
  const u = sunsetUTC(iso, lat, lng);
  return u == null ? null : ((u + offsetEast) % 1440 + 1440) % 1440;
}

/* Does an idea of this kind fit a free time? Outdoors wants two hours of daylight (an evening rarely has them). */
export function fits(slot, kind, sunset) {
  if (kind !== 'outdoors') return slot.all || slot.from >= 16 * 60 || slot.to - slot.from >= 90;
  if (slot.all) return true;
  return sunset != null && sunset - slot.from >= 120;
}
/* The day a place's page plans for: the one you came for (`want`, Home's date night or Swipe for Friday) when it's
   offered, else the first free time that fits — a dry one first for outdoors. */
export function placeSlot(slots, kind, { want, sunsetFor, skyFor } = {}) {
  const list = slots || [];
  const asked = want ? list.find((s) => s.date === want) : null;
  if (asked) return asked;
  const sun = (d) => (sunsetFor ? sunsetFor(d) : null), sky = (d) => (skyFor ? skyFor(d) : null);
  return (kind === 'outdoors' ? list.find((s) => !s.unknown && fits(s, kind, sun(s.date)) && sky(s.date) !== 'wet') : null)
    || list.find((s) => !s.unknown && fits(s, kind, sun(s.date))) || null;
}
/* One idea for each free time, from your ideas: a whole free day leans outdoors, an evening to dinner, drinks or
   something fun. With the weather (T3, `skyFor(date)` → 'good' | 'wet' | null): a fine day leads with outdoors, a
   wet one leaves it out. The ones you both like come first, then yours to suggest (`mine`: your partner is asked
   when you plan it); within those, a favourite first. `alts` are the others that fit, for Something else.
   → [{ slot, idea, alts }] */
export function ideasForSlots(slots, ideas, sunsetFor, skyFor) {
  const used = new Set();
  // the best ideas go to the times without a sitter first (withCare), then in date order
  const list = slots || [];
  const order = list.map((x, i) => i).sort((i, j) => (list[i].sitter ? 1 : 0) - (list[j].sitter ? 1 : 0) || i - j);
  const out = [];
  order.forEach((i) => { out[i] = pickFor(list[i]); });
  return out;
  function pickFor(slot) {
    const sun = sunsetFor ? sunsetFor(slot.date) : null;
    const sky = skyFor ? skyFor(slot.date) : null;
    const order = slot.all || (sky === 'good' && sun != null && sun - slot.from >= 180) ? ['outdoors', 'culture', 'fun', 'restaurant', 'bar'] : ['restaurant', 'bar', 'fun', 'culture', 'outdoors'];
    const kindOfIdea = (i) => normKind(i.kind || 'fun');
    const fit = (ideas || []).filter((i) => fits(slot, kindOfIdea(i), sun) && !(sky === 'wet' && kindOfIdea(i) === 'outdoors') && inSeason(i.months, slot.date));
    fit.sort((a, b) => (a.mine ? 1 : 0) - (b.mine ? 1 : 0) || order.indexOf(kindOfIdea(a)) - order.indexOf(kindOfIdea(b)) || (b.loved ? 1 : 0) - (a.loved ? 1 : 0));
    const pick = fit.find((i) => !used.has(i.id)) || null;
    if (pick) used.add(pick.id);
    return { slot, idea: pick, alts: fit.filter((i) => !pick || i.id !== pick.id) };
  }
}
/* Open that month: the catalogue's `open_months` (a beach, a summer patio); null is all year. */
export const inSeason = (months, iso) => !months || !months.length || months.includes(Number(String(iso).slice(5, 7)));
/* Somewhere inside instead of a rained-off hike: the ideas you both like that aren't outdoors, a gallery or a
   show first (it's daytime), then something fun, then lunch, then a bar; none already planned that day. */
export function indoorPicks(ideas, plannedIds = []) {
  const order = ['culture', 'fun', 'restaurant', 'bar'];
  return (ideas || []).filter((i) => i.kind && normKind(i.kind) !== 'outdoors' && !i.mine && !plannedIds.includes(i.id))
    .sort((a, b) => order.indexOf(normKind(a.kind)) - order.indexOf(normKind(b.kind)));
}
/* "Four free times this week" / "None of them planned yet." */
const NUM = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven'];
export function weekLede(n, planned) {
  const head = n ? (NUM[n] || n) + ' free time' + (n === 1 ? '' : 's') + ' this week' : 'No free time together this week';
  const sub = !n ? 'When the week opens up, an idea for it shows here.' : planned ? (planned === n ? 'All of them planned.' : planned + ' planned. One idea for the rest, from the ones you both like.')
    : (n === 1 ? 'Not planned yet. An idea for it, from the ones you both like.' : 'None of them planned yet. One idea for each, from the ones you both like.');
  return { head, sub };
}

/* ---- Plan it ---- */
/* Plan it's line under the days: "Saturday: you're both free all day." / "Thursday from 6:30pm. It's dark by
   then, so a walk in daylight won't fit." */
export function dayNote(slot, kind, sunset, words, wx) {
  const dark = kind === 'outdoors' && !slot.all && !(sunset != null && sunset - slot.from >= 120);
  const base = slot.all ? slot.dow + ': you’re both free all day.'
    : slot.dow + ' from ' + ampm(slot.from) + '.' + (dark ? ' It’s dark by then, so a ' + (words && words.hike === 'walk' ? 'walk' : 'hike') + ' in daylight won’t fit.' : '');
  // with children: who has them, or that it needs a sitter (withCare)
  const kids = slot.kids ? ' ' + slot.kids + '.' : '';
  // the forecast, for something outdoors in daylight (T3; `wx`: { words, wet })
  if (kind !== 'outdoors' || dark || !wx || !wx.words) return base + kids;
  return base + kids + (wx.wet ? ' ' + wx.wet + ' forecast: ' + wx.words + '.' : ' The forecast: ' + wx.words + '.');
}
/* When to start and how long: an evening from when you're both free (to the half hour); a whole free day at
   10am for outdoors, 1pm for out, 7pm for dinner and 8pm for drinks. */
export function planDefaults(kind, slot, cat) {
  const minutes = (cat && cat.minutes) || USUAL_MINUTES[kind] || 120;
  let start;
  if (slot && !slot.all) start = Math.ceil(slot.from / 30) * 30;
  else start = { outdoors: 600, culture: 780, out: 780, fun: 840, restaurant: 1140, bar: 1200 }[kind] || 1140;
  return { start, minutes };
}
export const hoursWords = (m) => (m % 60 ? (m / 60).toFixed(1).replace(/\.0$/, '') + ' hours' : m === 60 ? 'An hour' : m / 60 + ' hours');
export const DURATIONS = [60, 90, 120, 150, 180, 240, 300];

/* ---- the catalogue ---- */
export const townName = (town) => String(town || '').split(',')[0].trim();   // 'Hamilton, Ontario' → 'Hamilton'
/* A booking note shows until its date (Spencer Gorge's parking until 8 Nov). */
export const bookingLive = (cat, today) => !!(cat && cat.booking_note && (!cat.booking_until || cat.booking_until >= today));

/* ---- words by country (couples.country_code) ---- */
export function words(cc) {
  const c = String(cc || 'CA').toUpperCase();
  return {
    hike: c === 'GB' ? 'walk' : 'hike',
    hikes: c === 'GB' ? 'Walks' : 'Hikes',
    films: c === 'GB' ? 'In cinemas' : c === 'US' ? 'In theaters' : 'In theatres',
    miles: c === 'US' || c === 'GB',
    reserve: c === 'US' ? 'Reserve' : 'Book',
    country: c,
  };
}
/* A walk in numbers, in the units people use there: "4.8 km there and back · easy" (Canada), "3 mile loop ·
   moderate" (the UK and the US). Otherwise our own outing line, if there is one. */
export function outingWords(cat, words) {
  if (!cat) return null;
  if (cat.trail_km) {
    const miles = !!(words && words.miles);
    const n = miles ? Math.round(cat.trail_km * 0.621371 * 10) / 10 : Math.round(cat.trail_km * 10) / 10;
    const num = String(n).replace(/\.0$/, '');
    const shape = cat.trail_shape === 'loop' ? (miles ? num + ' mile loop' : num + ' km loop')
      : (miles ? num + (n === 1 ? ' mile' : ' miles') : num + ' km') + (cat.trail_shape === 'one_way' ? ' one way' : cat.trail_shape === 'out_and_back' ? ' there and back' : '');
    return [shape, cat.effort || null, cat.outing || null].filter(Boolean).join(' · ');
  }
  return cat.outing || null;
}
/* What a planned date is called on the week. Our catalogue's name when we have the place; otherwise our own word for
   the kind of place ("Dinner out", "Bowling", "A museum"), since Google's terms let us keep a place's id but not its
   name. The name shows live. The ask reads it mid-sentence: "Ben's up for bowling on Friday". */
const TYPE_TITLE = {
  bowling_alley: 'Bowling', escape_room: 'An escape room', karaoke: 'Karaoke', video_arcade: 'An arcade', miniature_golf_course: 'Mini golf',
  go_karting_venue: 'Go-karting', ice_skating_rink: 'Skating', amusement_park: 'A day at the fair', aquarium: 'The aquarium', zoo: 'The zoo',
  museum: 'A museum', art_museum: 'A gallery', history_museum: 'A museum', art_gallery: 'A gallery', performing_arts_theater: 'A show',
  concert_hall: 'A concert', opera_house: 'The opera', comedy_club: 'A comedy night', live_music_venue: 'Live music', jazz_club: 'Live music',
};
export function planTitle(kind, cat, words, type) {
  if (cat && cat.name) return cat.name;
  if (type && TYPE_TITLE[type]) return TYPE_TITLE[type];
  if (type === 'movie_theater') return words && words.country === 'US' ? 'The movies' : 'The cinema';
  return { restaurant: 'Dinner out', bar: 'Drinks out', outdoors: words && words.hike === 'walk' ? 'A walk' : 'A hike', fun: 'Something fun', culture: 'Out together', out: 'Out together' }[normKind(kind)] || 'A date';
}

/* ---- the catalogue's areas near home ---- */
// Roughly how far a drive of that long gets you (as courted-places' box).
const REACH = { 15: 12, 30: 25, 60: 55, 90: 85 };
export function kmBetween(a, b) {
  const R = 6371, d = (x) => x * Math.PI / 180;
  const h = Math.sin(d(b.lat - a.lat) / 2) ** 2 + Math.cos(d(a.lat)) * Math.cos(d(b.lat)) * Math.sin(d(b.lng - a.lng) / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}
/* The areas whose places you could reach: the centre within the area's radius plus how far you'll go (or, around
   a town, plus how far the town reaches). → keys, nearest first. */
export function areasNear(areas, home, range, km) {
  if (!home) return [];
  const reach = km != null ? km : REACH[range] || 25;
  return (areas || []).map((a) => ({ k: a.key, km: kmBetween(home, { lat: Number(a.lat), lng: Number(a.lng) }), r: Number(a.radius_km) || 20 }))
    .filter((a) => a.km <= a.r + reach).sort((x, y) => x.km - y.km).map((a) => a.k);
}
export const rangeWords = (range, town) => 'Up to about ' + (RANGES.find((r) => r[0] === range) || RANGES[1])[1].toLowerCase() + ' by car from ' + (town || 'home') + '.';

/* ---- In theatres ---- */
/* The films showing near you that you both said yes to in Curated (a home of one: yours), and otherwise
   what's on, most popular first. `swipes`: Curated's rows on those films. */
export function inTheatres(films, swipes, me, partner) {
  const yes = {};
  (swipes || []).forEach((r) => { if (r.decision === 'yes') (yes[r.tmdb_id] = yes[r.tmdb_id] || new Set()).add(r.user_id); });
  const both = (films || []).filter((f) => yes[f.id] && yes[f.id].has(me) && (!partner || yes[f.id].has(partner)));
  const showing = [...(films || [])].sort((a, b) => b.popularity - a.popularity).slice(0, 3);
  return { both, showing };
}
export const filmLine = (f) => [String((f.genres || [])[0] || '').toLowerCase().replace(/^./, (c) => c.toUpperCase()), f.runtime ? Math.floor(f.runtime / 60) + 'h ' + String(f.runtime % 60).padStart(2, '0') + 'm' : null].filter(Boolean).join(' · ');

/* ---- Go out anywhere (DOM-26): the towns you look at besides home ---- */
/* Which town each place was swiped in: the first answer made in a town says (none: near home). → { id: townId | null } */
export function placeTowns(rows) {
  const t = {};
  (rows || []).forEach((r) => { if (!(r.place_id in t) || (!t[r.place_id] && r.town_id)) t[r.place_id] = r.town_id || null; });
  return t;
}
/* The answers about places in one town (null: near home), so each place keeps its own yeses. */
export function rowsIn(rows, townId) {
  const t = placeTowns(rows);
  return (rows || []).filter((r) => (t[r.place_id] || null) === (townId || null));
}
/* The one line over the deck (Ben, 1 Nov: one row of controls, not three): what, where, and when.
   → "Everything near Hamilton", "Dinner in Toronto", "Dinner and drinks in Paris on Saturday" */
export function whereLine({ kinds, town, homeName, slot, today } = {}) {
  const picked = DATE_KINDS.filter(([k]) => (kinds || []).includes(k)).map(([, l]) => l);
  const what = !picked.length || picked.length === DATE_KINDS.length ? 'Everything'
    : picked.length === 1 ? picked[0]
    : picked.length === 2 ? picked[0] + ' and ' + picked[1].toLowerCase()
    : picked[0] + ', ' + picked[1].toLowerCase() + ' and ' + (picked.length - 2) + ' more';
  const where = town ? 'in ' + town.name : 'near ' + (homeName || 'home');
  const when = slot ? (slot.date === today ? 'tonight' : 'on ' + slot.dow) : '';
  return [what, where, when].filter(Boolean).join(' ');
}
/* Google's rating, as people read it: "4.6 (1,240)". */
export const ratingWords = (a) => (a && a.rating ? a.rating.toFixed(1) + (a.count ? ' (' + a.count.toLocaleString('en-US') + ')' : '') : '');
/* A town as a row says it: "Paris, France", "Paris, Ontario" (the first part of what tells it apart). */
export const townWords = (t) => (t ? t.name + (t.detail ? ', ' + String(t.detail).split(',')[0].trim() : '') : '');
/* "… in Toronto", searched from somewhere else: the town it names, to offer looking there instead. → 'Toronto' | null */
export const namedTown = (q) => { const m = /\bin\s+([A-Za-zÀ-ÿ'’. -]{2,40})$/i.exec(String(q || '').trim()); return m ? m[1].trim().replace(/(^|[\s-])\S/g, (c) => c.toUpperCase()) : null; };
